import { useState, useCallback, useEffect, useRef } from 'react';
import type { TabItem, TabsSelectionProps } from '../types';

interface UseTabsOptions extends TabsSelectionProps {
  items: TabItem[];
}

interface UseTabsReturn {
  currentTab: string;
  isControlled: boolean;
  selectTab: (tabId: string) => void;
  handleKeyDown: (e: React.KeyboardEvent, tabId: string) => void;
  tabRefs: React.MutableRefObject<Map<string, HTMLButtonElement>>;
}

/**
 * Hook that manages Tabs selection state and keyboard navigation.
 *
 * Supports controlled (activeTab + onTabChange) and uncontrolled
 * (defaultActiveTab) patterns. Implements WAI-ARIA keyboard navigation
 * with arrow keys, Home, and End.
 *
 * @param options - Configuration including items and selection props.
 * @returns Object with current tab state, selection handler, and keyboard handler.
 */
export const useTabs = ({
  items,
  activeTab,
  defaultActiveTab,
  onTabChange,
}: UseTabsOptions): UseTabsReturn => {
  const isControlled = activeTab !== undefined;
  const firstEnabledId = items.find((item) => !item.disabled)?.id ?? items[0]?.id ?? '';

  const [internalTab, setInternalTab] = useState(defaultActiveTab ?? firstEnabledId);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  useEffect(() => {
    if (isControlled && activeTab !== undefined) {
      setInternalTab(activeTab);
    }
  }, [isControlled, activeTab]);

  const currentTab = isControlled ? (activeTab ?? firstEnabledId) : internalTab;

  const selectTab = useCallback(
    (tabId: string) => {
      const item = items.find((i) => i.id === tabId);
      if (item?.disabled) return;

      if (!isControlled) {
        setInternalTab(tabId);
      }
      onTabChange?.(tabId);
    },
    [items, isControlled, onTabChange]
  );

  const getEnabledItems = useCallback(() => items.filter((i) => !i.disabled), [items]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, tabId: string) => {
      const enabledItems = getEnabledItems();
      const currentIndex = enabledItems.findIndex((i) => i.id === tabId);
      if (currentIndex === -1) return;

      let targetIndex: number | null = null;

      switch (e.key) {
        case 'ArrowRight':
          targetIndex = (currentIndex + 1) % enabledItems.length;
          break;
        case 'ArrowLeft':
          targetIndex = (currentIndex - 1 + enabledItems.length) % enabledItems.length;
          break;
        case 'Home':
          targetIndex = 0;
          break;
        case 'End':
          targetIndex = enabledItems.length - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      const targetId = enabledItems[targetIndex].id;
      selectTab(targetId);
      tabRefs.current.get(targetId)?.focus();
    },
    [getEnabledItems, selectTab]
  );

  return {
    currentTab,
    isControlled,
    selectTab,
    handleKeyDown,
    tabRefs,
  };
};
